/**
 * The Wrapped as a story, the way Instagram plays one (Marek, 2026-10-10): each slide plays by itself
 * for five seconds under its own bar at the top; a tap on the left edge goes back, a tap anywhere else
 * goes on; a finger held on the screen pauses it, and letting go plays on. It pauses while the page is
 * hidden too. At the last slide it stops and stays: nothing closes by itself.
 *
 * Pure, so the timing can be tested; the screen (screens/Wrapped.tsx) feeds it the frames and the touches.
 */

export const STORY = {
  /** Each slide, on the phone and in the video alike. */
  SLIDE_MS: 5_000,
  /** A touch let go sooner than this is a tap; longer, it was a hold. */
  TAP_MS: 250,
  /** The left part of the screen that goes back on a tap. */
  BACK_EDGE: 1 / 3,
  /** Sideways further than this is a swipe to the next or the last slide. */
  SWIPE_PX: 50,
} as const;

export interface StoryClock {
  /** The slide on screen. */
  index: number;
  /** How long it has played, 0 to SLIDE_MS. */
  elapsed: number;
  /** A finger is on the screen. */
  held: boolean;
  /** The page is out of sight. */
  hidden: boolean;
}

export const storyStart = (): StoryClock => ({ index: 0, elapsed: 0, held: false, hidden: false });

export function storyPaused(c: StoryClock): boolean {
  return c.held || c.hidden;
}

/**
 * `dt` ms later. `ready` is how many slides are drawn so far: the clock waits at the last one drawn,
 * and at the very last slide it stops at its end and stays.
 */
export function storyTick(c: StoryClock, dt: number, ready: number): StoryClock {
  if (storyPaused(c) || dt <= 0 || ready <= 0) return c;
  let index = Math.min(c.index, ready - 1);
  let elapsed = c.elapsed + dt;
  while (elapsed >= STORY.SLIDE_MS && index < ready - 1) {
    elapsed -= STORY.SLIDE_MS;
    index += 1;
  }
  return { ...c, index, elapsed: Math.min(elapsed, STORY.SLIDE_MS) };
}

/** On or back a slide, from its start. Back from the first starts it again. */
export function storyStep(c: StoryClock, step: 1 | -1, ready: number): StoryClock {
  const last = Math.max(0, ready - 1);
  const index = Math.max(0, Math.min(last, c.index + step));
  // On from the last slide: it shows to its end and stays there.
  if (step === 1 && c.index >= last) return { ...c, index: last, elapsed: STORY.SLIDE_MS };
  return { ...c, index, elapsed: 0 };
}

export function storyHold(c: StoryClock, held: boolean): StoryClock {
  return c.held === held ? c : { ...c, held };
}

export function storyHidden(c: StoryClock, hidden: boolean): StoryClock {
  return c.hidden === hidden ? c : { ...c, hidden };
}

/** How full each slide's bar is: the ones before full, the one on screen part way, the rest empty. */
export function storyBars(c: StoryClock, count: number): number[] {
  return Array.from({ length: count }, (_, i) => (i < c.index ? 1 : i > c.index ? 0 : Math.min(1, c.elapsed / STORY.SLIDE_MS)));
}

/**
 * What a touch let go meant: a tap on the left edge goes back, a tap anywhere else goes on, a swipe
 * goes the way it was swiped, and a hold (or a pull up or down) only paused.
 */
export function storyTouch(o: { x: number; width: number; dx: number; dy: number; ms: number }): 'back' | 'on' | 'none' {
  if (Math.abs(o.dx) > STORY.SWIPE_PX && Math.abs(o.dx) > Math.abs(o.dy)) return o.dx < 0 ? 'on' : 'back';
  if (o.ms >= STORY.TAP_MS || Math.abs(o.dx) > 12 || Math.abs(o.dy) > 12) return 'none';
  return o.x < o.width * STORY.BACK_EDGE ? 'back' : 'on';
}
